import 'wicg-inert';
import 'react-hot-loader';

import './i18n';
import './styles/index.scss';

import * as React from 'react';
import ReactDOM from 'react-dom';

import * as digitalData from '@ao-internal/digital-data';

import App, { ChatSession } from './App';
import { getMvtConfig } from './config';
import {
	TaggingEventNames,
	chatbotParentElId,
	isAuditMode,
	tabId,
} from './globals';
import { createAppElement } from './helpers/dom';
import { mergeRemoteAndLocalState } from './helpers/state';
import {
	getChatHistoryAuditAccessKey,
	getPersistentState,
	shouldRetrieveRemoteSession,
} from './helpers/storage';
import * as tabManager from './helpers/tab';
import { ChatMode, LiveChatStatus } from './store';

// Debug tagging
// import { captureGlobalEvents } from '@ao-internal/digital-data';
// captureGlobalEvents(document.body);
// window.debugCaptureGlobalEvents = true;

/* istanbul ignore file */

let mvtConfig = getMvtConfig();
let chatSession: ChatSession;
let isRendered = false;
let isInitialised = false;

export async function init(): Promise<void> {
	if (isAuditMode) {
		const auditAccessKey = getChatHistoryAuditAccessKey();

		if (!auditAccessKey) {
			const chatAuditWarning = document.getElementById('chat-audit-warning');

			if (chatAuditWarning) {
				chatAuditWarning.classList.remove('hidden');
			}
			return Promise.resolve();
		}
	}

	if (shouldRetrieveRemoteSession()) {
		chatSession = await mergeRemoteAndLocalState(getPersistentState());
	} else {
		chatSession = {
			chatInitialised: false,
			chatMode: ChatMode.ChatBot,
		} as ChatSession;
	}

	window.addEventListener('unload', () => {
		tabManager.unregisterTab(tabId);
	});

	isInitialised = true;

	const { liveChatStatus } = chatSession;

	if (
		liveChatStatus === LiveChatStatus.Connecting ||
		liveChatStatus === LiveChatStatus.Connected ||
		liveChatStatus === LiveChatStatus.Transferring
	) {
		// Make sure the toggle button will be visible when the app is rendered
		mvtConfig = { ...getMvtConfig(), hideToggleCta: false };

		window.AOChatbot.render();
	}

	window.dispatchEvent(new CustomEvent('chatbotReady'));

	return Promise.resolve();
}

window.AOChatbot = {
	render: async () => {
		if (!isRendered) {
			isRendered = true;
			if (!isInitialised) {
				await init();
			}

			digitalData.sendBeacon(TaggingEventNames.rendered, mvtConfig);

			ReactDOM.render(
				<App
					mvtConfig={mvtConfig}
					chatSession={{
						...chatSession,
						toastNotification: mvtConfig.toastNotification,
					}}
				/>,
				createAppElement(chatbotParentElId)
			);
		}
	},
	reload: async () => {
		const chatbotParentEl = document.getElementById(chatbotParentElId);
		document.documentElement.classList.remove('chatbot-widget--open');
		if (chatbotParentEl && chatbotParentEl.parentNode) {
			ReactDOM.unmountComponentAtNode(chatbotParentEl);
			chatbotParentEl.parentNode.removeChild(chatbotParentEl);
		}
		isRendered = false;
		init();
	},
};

init();
